import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Broadcast, X, ArrowRight } from "@phosphor-icons/react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";

/** Tiny red "LIVE" pulse badge. */
export function LiveBadge({ className = "" }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-600 text-white text-[10px] font-bold tracking-wider uppercase ${className}`}
      data-testid="live-badge"
    >
      <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
      Live
    </span>
  );
}

/** Modal for the broadcaster — either start a native LiveKit stream
 *  from this device's camera (recommended) OR paste a YouTube /
 *  Facebook / Twitch URL for legacy external broadcasts. */
export function GoLiveModal({ open, onClose, onLive }) {
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Visibility toggle — "public" puts the broadcast on the homepage
  // Live Now rail AND your channel; "channel" keeps it on your
  // channel only so it's a private session for your followers.
  const [visibility, setVisibility] = useState("public");
  const [nativeBusy, setNativeBusy] = useState(false);
  // Camera-check step: tapping "Broadcast from this device" opens a live
  // preview FIRST so the broadcaster sees themselves (and gets a clear,
  // actionable error if the laptop browser is blocking the camera) before
  // we ever connect a room. This is what kills the "black screen, no
  // camera, no message" laptop bug for good.
  const [checkOn, setCheckOn] = useState(false);

  // Step 2 — once the camera preview is confirmed working, create the
  // LiveKit room and jump to the broadcast page.
  const startNativeStream = async () => {
    setNativeBusy(true); setError("");
    try {
      const r = await api.post("/live/token", {
        role: "host",
        title: title || "Live with Holy Words",
        visibility,
      });
      onLive?.({ user_id: r.data.room_name?.replace(/^hw-/, ""), platform: "livekit" });
      setUrl(""); setTitle(""); setVisibility("public");
      onClose?.();
      try {
        const uid = r.data.room_name?.replace(/^hw-/, "");
        if (uid) window.location.assign(`/live/${uid}`);
      } catch (_) {}
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't start the live stream");
      setCheckOn(false);
    }
    setNativeBusy(false);
  };
  const submit = async () => {
    setBusy(true); setError("");
    try {
      const r = await api.post("/live/start", { url, title, visibility });
      onLive?.(r.data);
      setUrl(""); setTitle(""); setVisibility("public");
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't start the stream");
    }
    setBusy(false);
  };
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[110] grid place-items-center bg-ink/70 backdrop-blur-sm p-5"
      onClick={onClose}
      data-testid="go-live-modal"
    >
      <div className="bg-cream rounded-3xl max-w-md w-full overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-br from-red-600 to-rose-700 text-white p-5 flex items-center gap-2">
          <Broadcast size={22} weight="fill" />
          <span className="font-heading text-xl">Go Live on Holy Words</span>
          <button onClick={onClose} className="ml-auto opacity-80 hover:opacity-100"><X size={18} weight="bold" /></button>
        </div>
        <div className="p-5">
          {checkOn ? (
            <CameraCheck
              starting={nativeBusy}
              onReady={startNativeStream}
              onCancel={() => { setCheckOn(false); setError(""); }}
            />
          ) : (
          <>
          <label className="block text-[11px] tracking-wide uppercase text-muted_ink font-semibold">Title</label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder='"Wednesday Bible study" or "Sunday morning praise"'
            maxLength={120}
            className="mt-1 w-full px-4 h-11 rounded-2xl bg-white border border-cream-300 text-sm"
            data-testid="go-live-title"
          />

          {/* Visibility toggle removed — broadcasts always go out to
              Everyone (homepage Live Now rail + your channel). The old
              "My channel" (channel-only) option errored, so it's gone
              and visibility stays "public". */}

          {/* Primary CTA — native LiveKit camera broadcast. Most users
              will tap this and never touch the URL field. */}
          <button
            onClick={() => { setError(""); setCheckOn(true); }}
            disabled={nativeBusy}
            data-testid="go-live-native"
            className="w-full mt-4 px-5 h-12 rounded-full bg-red-600 hover:bg-red-700 text-white font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <Broadcast size={16} weight="fill" /> Broadcast from this device
          </button>
          <p className="text-[11px] text-muted_ink mt-2 leading-relaxed">
            Uses your phone or computer's camera + mic — no external app needed. Your followers see the stream live inside Holy Words.
          </p>

          {/* Secondary fallback — paste external stream URL */}
          <details className="mt-5 text-xs text-muted_ink">
            <summary className="cursor-pointer font-semibold text-ink">…or paste an external stream link</summary>
            <p className="mt-2 leading-relaxed">
              Already streaming on YouTube, Facebook Live, or Twitch? Paste the public link below and we'll embed it on your profile.
            </p>
            <label className="block text-[11px] tracking-wide uppercase font-semibold mt-3">Stream URL</label>
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="Paste your live stream link"
              className="mt-1 w-full px-4 h-11 rounded-2xl bg-white border border-cream-300 text-sm"
              data-testid="go-live-url"
            />
            <button
              onClick={submit}
              disabled={busy || !url}
              data-testid="go-live-submit"
              className="w-full mt-3 px-5 h-11 rounded-full bg-ink hover:bg-ink/85 text-cream font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {busy ? "Embedding…" : "Embed external stream"}
            </button>
          </details>

          {error && <p className="text-xs text-red-600 mt-3" data-testid="go-live-error">{error}</p>}
          </>
          )}
        </div>
      </div>
    </div>
  );
}

/** A horizontal "X people are live now" rail for the home page or header. */
export function LiveNowRail() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    const load = () => api.get("/live/now").then((r) => setItems(r.data.items || [])).catch(() => {});
    load();
    const id = setInterval(load, 30000);  // refresh every 30s
    return () => clearInterval(id);
  }, []);
  if (items.length === 0) {
    // Always show the section so people KNOW where lives appear —
    // an invisible rail made users think live discovery was broken.
    return (
      <section
        className="my-6 rounded-3xl p-4 md:p-5 border border-cream-300 bg-white"
        data-testid="live-now-rail-empty"
      >
        <div className="flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-1.5 px-2 h-6 rounded-full bg-red-600/10 text-red-700 text-[10px] font-bold tracking-[0.14em] uppercase">
            <span className="w-1.5 h-1.5 rounded-full bg-red-600" /> Live
          </span>
          <span className="text-sm text-muted_ink flex-1 min-w-[200px]">
            No one is live right now — broadcasts appear here the moment they start.
          </span>
          <Link
            to="/live"
            data-testid="live-empty-golive"
            className="inline-flex items-center gap-1 px-3 h-8 rounded-full bg-red-600 hover:bg-red-700 text-white text-xs font-bold transition-colors"
          >
            <Broadcast size={12} weight="fill" /> Go live
          </Link>
        </div>
      </section>
    );
  }
  return (
    <section
      className="my-6 rounded-3xl p-4 md:p-5 border border-red-200 bg-gradient-to-r from-red-50 to-rose-50"
      data-testid="live-now-rail"
    >
      <div className="flex items-center gap-2 mb-3">
        <LiveBadge />
        <span className="text-sm font-heading text-red-700">{items.length} {items.length === 1 ? "person is" : "people are"} live right now</span>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-1">
        {items.map((s) => (
          <Link
            key={s.user_id}
            to={`/live/${s.user_id}`}
            data-testid={`live-rail-${s.user_id}`}
            className="shrink-0 w-60 bg-white rounded-2xl border border-red-100 p-3 hover:shadow-md hover:-translate-y-0.5 transition-all"
          >
            <div className="aspect-video relative rounded-xl overflow-hidden bg-gradient-to-br from-red-700 to-rose-900 grid place-items-center">
              <Broadcast size={32} weight="fill" className="text-white opacity-80" />
              <LiveBadge className="absolute top-2 left-2" />
            </div>
            <div className="mt-2">
              <div className="font-heading text-sm text-forest truncate">{s.title}</div>
              <div className="text-[10px] text-muted_ink truncate flex items-center gap-1">
                {s.user_name} · <span className="capitalize">{s.platform}</span> <ArrowRight size={10} weight="bold" />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

/** Tiny red LIVE pill for the app header — visible on EVERY page while
 * someone is broadcasting publicly, so followers never have to scroll
 * to find a live. Tap → jump straight into the stream. */
export function LiveHeaderBadge() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    const load = () => api.get("/live/now").then((r) => setItems(r.data.items || [])).catch(() => {});
    load();
    const id = setInterval(load, 40000);
    return () => clearInterval(id);
  }, []);
  if (items.length === 0) return null;
  const first = items[0];
  return (
    <Link
      to={`/live/${first.user_id}`}
      data-testid="header-live-badge"
      title={`${first.user_name} is live: ${first.title}`}
      className="inline-flex items-center gap-1.5 px-2.5 h-9 rounded-full bg-red-600 hover:bg-red-700 text-white text-[10px] font-bold tracking-[0.14em] uppercase shadow-md transition-colors shrink-0"
    >
      <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
      Live{items.length > 1 ? ` · ${items.length}` : ""}
    </Link>
  );
}

/** Compact "Go Live" button (placed in header or profile). */
export function GoLiveButton({ className = "" }) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  if (!user) return null;
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="go-live-button"
        className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-red-600 hover:bg-red-700 text-white text-xs font-semibold ${className}`}
      >
        <Broadcast size={14} weight="fill" /> {t("nav.goLive")}
      </button>
      <GoLiveModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}


/** Live camera + mic preview shown before a native broadcast starts.
 *  Requests getUserMedia, mirrors the feed so the host literally sees
 *  themselves, and — when the browser blocks the camera — shows the
 *  exact reason + fix steps instead of a silent black broadcast page.
 *  On "Go Live now" it releases the preview stream so LiveKit can grab
 *  the camera cleanly, then hands control back to start the room. */
function CameraCheck({ onReady, onCancel, starting }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [status, setStatus] = useState("requesting"); // requesting | ready | error
  const [errMsg, setErrMsg] = useState("");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const stopStream = () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
    (async () => {
      setStatus("requesting"); setErrMsg("");
      try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          throw Object.assign(new Error("unsupported"), { name: "TypeError" });
        }
        const s = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        if (cancelled) { s.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = s;
        if (videoRef.current) {
          videoRef.current.srcObject = s;
          videoRef.current.play().catch(() => {});
        }
        setStatus("ready");
      } catch (e) {
        if (cancelled) return;
        const name = e?.name || "";
        if (name === "NotAllowedError" || name === "SecurityError") {
          setErrMsg("Camera & mic are blocked for this site. On a laptop: click the camera icon at the left of the address bar, choose Allow, then tap Retry.");
        } else if (name === "NotFoundError" || name === "OverconstrainedError") {
          setErrMsg("No camera or microphone was found. Connect one (or enable it in your system settings) and tap Retry.");
        } else if (name === "NotReadableError") {
          setErrMsg("Your camera is being used by another app or tab (Zoom, Teams, Meet…). Close it and tap Retry.");
        } else if (name === "TypeError") {
          setErrMsg("This browser is blocking camera access. Use Chrome or Edge, make sure the address bar shows https, then tap Retry.");
        } else {
          setErrMsg("Couldn't access your camera & mic. Check your browser permissions and tap Retry.");
        }
        setStatus("error");
      }
    })();
    return () => { cancelled = true; stopStream(); };
  }, [attempt]);

  const goLive = () => {
    // Release the preview camera so LiveKit can re-acquire it without a
    // "device in use" collision on the broadcast page. On Windows the OS
    // needs a moment to actually free the device after .stop(), so we
    // wait briefly before handing off — otherwise LiveKit grabs a busy
    // camera and the host sees the camera-off placeholder with the light
    // still on.
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setTimeout(() => { onReady?.(); }, 450);
  };

  return (
    <div data-testid="camera-check">
      <div className="relative w-full rounded-2xl overflow-hidden bg-black" style={{ aspectRatio: "16 / 9" }}>
        <video
          ref={videoRef}
          muted
          playsInline
          autoPlay
          data-testid="camera-check-preview"
          className="w-full h-full object-cover"
          style={{ transform: "scaleX(-1)" }}
        />
        {status === "requesting" && (
          <div className="absolute inset-0 grid place-items-center text-cream/80 text-sm bg-black/60">
            Allow camera & mic when your browser asks…
          </div>
        )}
        {status === "error" && (
          <div className="absolute inset-0 grid place-items-center text-cream/70 bg-black/70">
            <Broadcast size={36} weight="duotone" />
          </div>
        )}
        {status === "ready" && (
          <span className="absolute top-2 left-2 inline-flex items-center gap-1.5 px-2 h-6 rounded-full bg-emerald-600 text-white text-[10px] font-bold tracking-wider uppercase" data-testid="camera-check-ready">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Camera ready
          </span>
        )}
      </div>

      {status === "error" && (
        <p className="text-xs text-red-600 mt-3 leading-relaxed" data-testid="camera-check-error">{errMsg}</p>
      )}

      <div className="mt-4 flex gap-2">
        <button
          onClick={onCancel}
          data-testid="camera-check-cancel"
          className="px-4 h-11 rounded-full bg-white border border-cream-300 text-ink text-sm font-semibold hover:border-forest"
        >
          Back
        </button>
        {status === "error" ? (
          <button
            onClick={() => setAttempt((n) => n + 1)}
            data-testid="camera-check-retry"
            className="flex-1 px-5 h-11 rounded-full bg-forest hover:bg-forest-600 text-cream font-semibold text-sm"
          >
            Retry camera
          </button>
        ) : (
          <button
            onClick={goLive}
            disabled={status !== "ready" || starting}
            data-testid="camera-check-golive"
            className="flex-1 px-5 h-11 rounded-full bg-red-600 hover:bg-red-700 text-white font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <Broadcast size={16} weight="fill" /> {starting ? "Starting…" : "Go Live now"}
          </button>
        )}
      </div>
    </div>
  );
}
